import React from 'react';
import Box from '@elementor/ui/Box';
import Container from '@elementor/ui/Container';
import Typography from '@elementor/ui/Typography';
import Chip from '@elementor/ui/Chip';
import { ArrowDownSmallIcon } from '@elementor/icons';
// import { GiftIcon } from '@elementor/icons';
import SearchInput from './SearchInput';
import heroImage from '../assets/Image.png';
import { getAssetUrl, pushDataLayerEvent } from '../helpers';

function isElementorBlock(el) {
  return (
    el.classList?.contains('elementor-element') ||
    el.classList?.contains('e-con') ||
    el.tagName === 'SECTION'
  );
}

function nextElementorSibling(el) {
  let next = el.nextElementSibling;
  while (next) {
    if (isElementorBlock(next)) return next;
    next = next.nextElementSibling;
  }
  return null;
}

function scrollToNextSection() {
  const root = document.getElementById('ft-root');
  let target = null;
  let node = root;
  while (node && node !== document.body) {
    target = nextElementorSibling(node);
    if (target) break;
    node = node.parentElement;
  }

  const anchor = target || document.querySelector('.ft-hero-wrapper');
  if (!anchor) return;

  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  if (target) {
    target.scrollIntoView({ behavior, block: 'start' });
  } else {
    anchor.scrollIntoView({ behavior, block: 'end' });
  }
}

const SUGGESTED_QUESTIONS = [
  'How do I fix a 500 server error?',
  'How do I hide the page title?',
  'How do I fix container alignment on mobile?',
];

/**
 * Initial search page with hero section and search bar
 * Using Elementor UI components
 */
function SearchPage({ onSearch, isLoading }) {
  const handleHeroSearch = (text) => {
    onSearch(text, 'hero');
  };

  const handleChipClick = (question) => {
    onSearch(question, 'chip');
  };

  const handleExploreMore = () => {
    pushDataLayerEvent({
      section: 'hero',
      element_name: 'explore more',
      outcome: 'scrolling to content below',
      english_text: 'Explore more',
    });
    scrollToNextSection();
  };

  // const handleBirthdaySaleClick = () => {
  //   pushDataLayerEvent({
  //     section: 'hero section',
  //     element_name: 'birthday sale button',
  //     outcome: 'navigating to sale page',
  //   });
  //   window.open(
  //     'https://elementor.com/products/website-builder/?utm_source=knowledge_hub&utm_medium=button&utm_campaign=birthday#plans',
  //     '_blank',
  //     'noopener,noreferrer'
  //   );
  // };

  return (
    <Box
      className="ft-hero-wrapper"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'grey.50',
        borderRadius: 2,
        pt: { xs: 6, md: 7 },
        pb: { xs: '12px !important', md: '16px !important' },
        px: { xs: 3, md: 7 },
      }}
    >
      <Container maxWidth="l">
        <Box
          className="ft-hero-grid"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 4, md: 11 },
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left Column — Content */}
          <Box
            className="ft-hero-content"
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 50%' },
              maxWidth: { xs: '100%', md: '50%' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 5,
            }}
          >
            {/* Heading + Subtitle group */}
            <Box className="ft-hero-heading-group" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Hero Title */}
              <Typography
                className="ft-hero-title"
                component="h1"
                variant="h2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 650,
                  lineHeight: 1.1,
                  textAlign: { xs: 'center', md: 'left' },
                  typography: { xs: 'h4', md: 'h2' },
                }}
              >
                How can we help?
              </Typography>

              {/* Subtitle */}
              <Typography
                className="ft-hero-subtitle"
                variant="body1"
                sx={{
                  color: 'text.primary',
                  textAlign: { xs: 'center', md: 'left' },
                  fontSize: 20,
                }}
              >
                Detailed questions lead to better answers.
              </Typography>
            </Box>

            {/* Search Input + Suggestion Chips */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <SearchInput
                onSubmit={handleHeroSearch}
                placeholder="Search for whatever you want to learn..."
                isLoading={isLoading}
                showSubmitButton
              />
              <Box
                className="ft-hero-chips"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {SUGGESTED_QUESTIONS.map((question) => (
                  <Chip
                    key={question}
                    label={question}
                    variant="outlined"
                    shape="rounded"
                    clickable
                    onClick={() => handleChipClick(question)}
                    sx={{
                      backgroundColor: '#FFFFFF',
                      borderColor: '#D1D1D1',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.75,
                      '&:hover': {
                        backgroundColor: '#F6F6F6 !important',
                      },
                    }}
                  />
                ))}
                {/* Birthday Sale CTA */}
                {/* <svg width={0} height={0} style={{ position: 'absolute' }}>
                  <defs>
                    <linearGradient id="birthday-gradient" x1="100%" y1="0%" x2="0%" y2="0%">
                      <stop offset="0%" stopColor="#CE84FF" />
                      <stop offset="100%" stopColor="#F125ED" />
                    </linearGradient>
                  </defs>
                </svg>
                <Chip
                  label="Birthday Sale - Claim Discount"
                  icon={<GiftIcon sx={{ fill: 'url(#birthday-gradient)' }} />}
                  variant="outlined"
                  shape="rounded"
                  clickable
                  onClick={handleBirthdaySaleClick}
                  className="ft-birthday-sale-btn"
                  sx={{
                    flexDirection: 'row-reverse',
                    backgroundColor: '#FFFFFF',
                    borderColor: '#D1D1D1',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.75,
                    '& .MuiChip-label': {
                      background: 'linear-gradient(270deg, #CE84FF -18.3%, #F125ED 87.13%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    },
                    '& .MuiChip-icon': {
                      fontSize: '1rem',
                      ml: 0.5,
                      mr: 0,
                    },
                    '&:hover': {
                      backgroundColor: '#F6F6F6 !important',
                    },
                  }}
                /> */}
              </Box>
            </Box>
          </Box>

          {/* Right Column — Hero Image (desktop only) */}
          <Box
            className="ft-hero-image-wrapper"
            sx={{
              flex: '1 1 50%',
              maxWidth: '50%',
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'flex-end',
              overflow: 'hidden',
              borderRadius: 2,
            }}
          >
            <Box
              className="ft-hero-image"
              component="img"
              src={getAssetUrl(heroImage)}
              alt="Elementor showcase"
              sx={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 2,
              }}
            />
          </Box>
        </Box>
      </Container>
      <Box
        className="ft-explore-more"
        component="button"
        type="button"
        onClick={handleExploreMore}
        sx={{
          mt: { xs: 3, md: 4 },
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          color: 'text.primary',
          font: 'inherit',
          fontFamily: 'var(--Roobert, Roobert), sans-serif !important',
          fontSize: '1rem !important',
          fontWeight: '400 !important',
          letterSpacing: '0 !important',
          lineHeight: '1 !important',
          textTransform: 'none',
          p: 0,
          '& .ft-explore-more-icon': {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#000',
            color: '#fff',
            transition: 'background-color 0.2s ease',
            '& .MuiSvgIcon-root': {
              fontSize: 20,
              transition: 'transform 0.2s ease',
            },
          },
          '&:is(:hover, :focus-visible) .ft-explore-more-icon': {
            backgroundColor: '#ED01EE',
            '& .MuiSvgIcon-root': {
              transform: 'translateY(2px)',
            },
          },
        }}
      >
        Explore more
        <Box component="span" className="ft-explore-more-icon">
          <ArrowDownSmallIcon />
        </Box>
      </Box>
    </Box>
  );
}

export default SearchPage;
